export default class Router {
	constructor (VueRouter, routes = [], options = {}) {
		this._routes = [...routes]
		this._options = options
		this._context = {}
		this._middlewares = []
		this._globalMiddleware = this.routerOptions['middlewares'] ? this.routerOptions['middlewares'] : []
		this._compiledRoutesCache = []
		this._router = new VueRouter({
			...this.VueRouterOptions,
			routes: [...this.routes, { path: '*', redirect: '/home' }]
		})

		this._router.beforeEach(this._interceptor.bind(this))

		return Object.freeze({
			VueRouter: this._router,
			routes: this.routes,
			currentMiddlewares: [...this._globalMiddleware, ...this._middlewares],
			beforeEach: this.beforeEach.bind(this)
		})
	}

	get routes () {
		if (!this._compiledRoutesCache.length) {
			this._compiledRoutesCache = this._routes.map(route => this._compileRoute(route))
		}
		return this._compiledRoutesCache
	}

	_compileRoute (route) {
		if (route._children && route._children.length) {
			route._children = route._children.map(childRoute => this._compileRoute(childRoute))
		}
		return route._compile()
	}

	get routerOptions () {
		return this.filter(this._options, ['middlewares'])
	}

	get VueRouterOptions () {
		return this.filter(this._options, ['mode', 'scrollBehavior'])
	}

	_interceptor (to, from, next) {
		this._middlewares = []
		this._context = { to, from, next, router: this._router }
		this._mapMiddleware(to)
		this._applyMiddlewares(this._context)
	}

	_mapMiddleware (to) {
		if (to.matched.some(record => record.meta.middlewares || record.meta.middleware)) {
			to.matched.forEach(record => {
				if (record.meta.middlewares) {
					if (Array.isArray(record.meta.middlewares)) {
						this._middlewares.push(...record.meta.middlewares)
					}
				} else if (record.meta.middleware) {
					this._middlewares.push(record.meta.middleware)
				}
			})
		}
	}

	_applyMiddlewares (context) {
		const middlewares = [...this._globalMiddleware, ...this._middlewares]
		if (middlewares.length > 0) {
			const nextMiddleware = this._nextFactory(context, middlewares, 1)
			middlewares[0].handler({ ...context, next: nextMiddleware })
		} else {
			return context.next()
		}
	}

	_nextFactory (context, middleware, index) {
		const subsequentMiddleware = middleware[index]
		if (!subsequentMiddleware) {
			// Final next - use stored redirect args if any were captured
			return (...parameters) => {
				if (parameters.length > 0) {
					return context.next(...parameters)
				}
				if (context._redirectArgs) {
					return context.next(...context._redirectArgs)
				}
				return context.next()
			}
		}
		return (...parameters) => {
			// Store redirect args in context when middleware passes them
			if (parameters.length > 0) {
				context._redirectArgs = parameters
			}
			const nextMiddleware = this._nextFactory(context, middleware, index + 1)
			subsequentMiddleware.handler({ ...context, next: nextMiddleware })
		}
	}

	beforeEach (handler) {
		this._router.beforeEach((to, from, next) => {
			handler({ to, from, next })
		})
	}

	filter (obj, keys) {
		return Object.assign({}, ...Object.keys(obj).filter(key => keys.includes(key)).map(key => ({ [key]: obj[key] })))
	}
}
